import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { AlertService } from '../../shared/alert/alert.service';
import { CategoryModel, EntriesConfig, EntryModel } from '../finance.model';
import { FinanceStoreService } from '../finance-store.service';
import { ComputedLine, FinanceSummaryService, sum } from '../finance-summary.service';
import { MonthService, monthLabel, shiftMonth } from '../../shared/month/month.service';
import { EntryDialogComponent, EntryDialogData } from './entry-dialog.component';

// Incomes, Bills and Expenses: same screen, configured by the route data
@Component({
  selector: 'finance-entries',
  standalone: false,
  templateUrl: './entries.component.html',
  styleUrl: '../finances-list.scss'
})
export class EntriesComponent implements OnInit, OnDestroy {
  icons = environment.icons;
  config: EntriesConfig;
  entries: EntryModel[] = [];
  computed: ComputedLine[] = [];
  // Entries followed by the read-only computed lines
  rows: (EntryModel | (ComputedLine & { computed: true }))[] = [];
  categories: CategoryModel[] = [];
  columns = ['description', 'category', 'value', 'action'];
  isLoading = false;

  months = inject(MonthService);
  private store = inject(FinanceStoreService);
  private summary = inject(FinanceSummaryService);
  private dialog = inject(MatDialog);
  private alert = inject(AlertService);
  private subscription?: Subscription;

  constructor(route: ActivatedRoute) {
    this.config = route.snapshot.data as EntriesConfig;
  }

  async ngOnInit(): Promise<void> {
    const categories = await this.store.list<CategoryModel>('FinanceCategories');
    this.categories = categories
      .filter(c => c.group == this.config.group && c.active)
      .sort((a, b) => a.name.localeCompare(b.name));

    this.subscription = this.months.month$.subscribe(() => this.load());
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  get total(): number {
    return sum([...this.entries, ...this.computed]);
  }

  async load(): Promise<void> {
    const month = this.months.month;
    this.isLoading = true;
    try {
      const [entries, computed] = await Promise.all([
        this.store.list<EntryModel>(this.config.collection, month),
        this.computedLines(month)
      ]);
      // Ignore a response of a month that is no longer selected
      if (month != this.months.month) return;
      this.entries = entries.sort((a, b) => a.description.localeCompare(b.description));
      this.computed = computed;
      this.rows = [...this.entries, ...computed.map(c => ({ ...c, computed: true as const }))];
    } catch {
      this.alert.show('Load failed!', 'error');
    } finally {
      this.isLoading = false;
    }
  }

  private async computedLines(month: string): Promise<ComputedLine[]> {
    if (this.config.collection == 'Incomes') {
      const dividends = await this.summary.dividends(month);
      return dividends ? [{ description: 'Dividendos (Investments)', category: 'Dividendos', value: dividends }] : [];
    }
    if (this.config.collection == 'Expenses')
      return this.summary.cardLines(month);
    return [];
  }

  edit(entry?: EntryModel): void {
    const data: EntryDialogData = { title: this.config.title, entry: { ...entry }, categories: this.categories };
    this.dialog.open(EntryDialogComponent, { data }).afterClosed().subscribe(async (result?: EntryModel) => {
      if (!result) return;
      try {
        if (result.id) await this.store.update(this.config.collection, result);
        else await this.store.add(this.config.collection, { ...result, month: this.months.month });
        this.alert.show('Saved!', 'save_as');
        await this.load();
      } catch {
        this.alert.show('Save failed!', 'error');
      }
    });
  }

  async delete(entry: EntryModel): Promise<void> {
    try {
      await this.store.remove(this.config.collection, entry.id);
      this.alert.show('Deleted!', 'delete_forever');
      await this.load();
    } catch {
      this.alert.show('Delete failed!', 'error');
    }
  }

  // Brings last month's entries that are not in the current month yet
  async copyPrevious(): Promise<void> {
    const month = this.months.month;
    const previous = shiftMonth(month, -1);
    try {
      const entries = await this.store.list<EntryModel>(this.config.collection, previous);
      const missing = entries.filter(e => !this.entries.some(c => c.description.toLowerCase() == e.description.toLowerCase()));
      if (!missing.length) {
        this.alert.show(`Nothing to copy from ${monthLabel(previous)}`, 'info');
        return;
      }

      for (const entry of missing)
        await this.store.add(this.config.collection, { description: entry.description, category: entry.category, value: entry.value, month });
      this.alert.show(`${missing.length} copied!`, 'content_copy');
      await this.load();
    } catch {
      this.alert.show('Copy failed!', 'error');
    }
  }
}
